import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, DollarSign, Users, Download, FileText, PieChart, BarChart, Play, Target, Lightbulb, Rocket, MapPin, Calendar, Star } from 'lucide-react';

const InteractiveDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "123zzz",
      subtitle: "The First Safe & Meaningful Animated Stories Channel for Families",
      content: (
        <div className="text-center space-y-8">
          <div className="relative">
            <motion.div
              className="text-9xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              123zzz
            </motion.div>
            <motion.div 
              className="text-2xl font-bold text-slate-700 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              The First Safe & Meaningful Animated Stories Channel for Families
            </motion.div>
            <motion.div 
              className="text-lg text-slate-600 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Designed to Help Families Avoid YouTube and Empower Educators
            </motion.div>
          </div>
          
          <motion.div 
            className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <p className="text-xl text-slate-600 leading-relaxed">
              AI-powered, emotionally intelligent stories that grow with your child and support values-based learning in schools
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-4 gap-8 max-w-3xl mx-auto mt-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            {[
              { label: "Family Safe", color: "bg-blue-500" },
              { label: "Educator Ready", color: "bg-green-500" },
              { label: "Values Based", color: "bg-purple-500" },
              { label: "Grows with Child", color: "bg-orange-500" }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 + index * 0.1 }}
              >
                <div className={`w-16 h-16 ${feature.color} rounded-2xl mx-auto mb-3 flex items-center justify-center`}>
                  <div className="w-8 h-8 bg-white rounded-lg"></div>
                </div>
                <div className="text-sm font-semibold text-slate-700">{feature.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      )
    },
    {
      id: 2,
      title: "The Problem",
      subtitle: "Current Digital Landscape Fails Families and Educators",
      content: (
        <div className="space-y-12">
          <div className="grid grid-cols-3 gap-8">
            {[
              { 
                stat: "75%", 
                desc: "of parents are concerned about harmful online content",
                color: "from-red-500 to-red-600"
              },
              { 
                stat: "4%", 
                desc: "of YouTube content is truly educational",
                color: "from-orange-500 to-orange-600"
              },
              { 
                stat: "Hours", 
                desc: "kids spend on platforms not designed for their development",
                color: "from-purple-500 to-purple-600"
              }
            ].map((problem, index) => (
              <motion.div
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/30 text-center shadow-xl"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`text-6xl font-black mb-4 bg-gradient-to-r ${problem.color} bg-clip-text text-transparent`}>
                  {problem.stat}
                </div>
                <p className="text-slate-700 text-lg leading-relaxed font-medium">{problem.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="bg-gradient-to-r from-slate-100 to-slate-200 rounded-3xl p-12 border-l-8 border-red-500 shadow-xl"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-3xl font-bold text-slate-800 mb-6">The Core Challenge</h3>
              <p className="text-xl text-slate-700 leading-relaxed">
                Parents and educators want safe, educational content that bonds families and supports learning, 
                but current platforms prioritize engagement over development, leaving children exposed to 
                inappropriate content and mindless consumption.
              </p>
            </div>
          </motion.div>
        </div>
      )
    },
    {
      id: 3,
      title: "Our Solution",
      subtitle: "AI-Powered Storytelling Where Kids Become Heroes",
      content: (
        <div className="space-y-12">
          <motion.div 
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl p-12 text-white text-center shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-4xl font-bold mb-8">
              123zzz is an AI-powered storytelling platform where kids become the heroes of their own animated stories
            </h3>
            <div className="grid grid-cols-2 gap-8 text-left max-w-4xl mx-auto">
              <div>
                <h4 className="text-2xl font-bold mb-4">For Families</h4>
                <ul className="space-y-2 text-lg">
                  <li>• Personalized bedtime stories</li>
                  <li>• Safe, educational content</li>
                  <li>• Emotional intelligence development</li>
                </ul>
              </div>
              <div>
                <h4 className="text-2xl font-bold mb-4">For Educators</h4>
                <ul className="space-y-2 text-lg">
                  <li>• SEL (Social Emotional Learning) tool</li>
                  <li>• Values-based curriculum support</li>
                  <li>• Instant classroom deployment</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-12">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mr-6">
                    <div className="w-8 h-8 bg-white rounded-lg"></div>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-800">Agentic AI</h4>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Remembers, adapts, and evolves with each child's learning journey and emotional development
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mr-6">
                    <div className="w-8 h-8 bg-white rounded-lg"></div>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-800">Safe by Design</h4>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Built with child safety, family bonding, and meaningful screen time as core principles
                </p>
              </div>
            </motion.div>

            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mr-6">
                    <div className="w-8 h-8 bg-white rounded-lg"></div>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-800">Educational Impact</h4>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Stories become personalized, pedagogical, and emotionally engaging learning experiences
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mr-6">
                    <div className="w-8 h-8 bg-white rounded-lg"></div>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-800">Dual Environment</h4>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Seamlessly works in homes for families and classrooms for educators
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "Product Experience",
      subtitle: "Luna's Magical Story - A Complete Journey",
      content: (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-16 items-center">
            {/* Mobile Mockup */}
            <motion.div
              className="relative flex justify-center"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Phone Frame */}
              <div className="relative">
                <div className="w-80 h-[640px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-2xl z-10"></div>
                    
                    {/* Screen Content */}
                    <div className="h-full bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex flex-col">
                      {/* Header */}
                      <div className="pt-8 pb-4 px-6 text-center">
                        <h3 className="text-xl font-bold text-slate-800">Luna's Adventure</h3>
                        <p className="text-sm text-slate-600">Personalized Story</p>
                      </div>
                      
                      {/* Story Image */}
                      <div className="flex-1 px-6 pb-4">
                        <div className="bg-white rounded-2xl shadow-lg p-4 h-full flex flex-col">
                          <div className="flex-1 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl mb-4 flex items-center justify-center relative overflow-hidden">
                            <img 
                              src="https://res.cloudinary.com/dogguvwef/image/upload/v1751434685/Luna1_hze4ue.png"
                              alt="Luna Story"
                              className="w-full h-full object-cover rounded-xl"
                            />
                          </div>
                          
                          {/* Story Text */}
                          <div className="bg-blue-50 rounded-xl p-3 mb-3">
                            <p className="text-xs text-slate-700 leading-relaxed">
                              "Luna the little fox had the fluffiest orange tail in all of Whispering Forest..."
                            </p>
                          </div>
                          
                          {/* Controls */}
                          <div className="flex items-center justify-between">
                            <button className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                              <div className="w-0 h-0 border-l-[6px] border-l-white border-y-[3px] border-y-transparent ml-0.5"></div>
                            </button>
                            <div className="text-xs text-slate-500">00:32 / 02:15</div>
                            <button className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                              <div className="w-3 h-3 bg-white rounded-full"></div>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bottom Navigation */}
                      <div className="px-6 pb-6">
                        <div className="bg-white rounded-2xl shadow-lg p-4">
                          <div className="flex justify-around">
                            <div className="text-center">
                              <div className="w-10 h-10 bg-blue-500 rounded-xl mb-1 flex items-center justify-center">
                                <div className="w-5 h-5 bg-white rounded"></div>
                              </div>
                              <span className="text-xs text-slate-600">Stories</span>
                            </div>
                            <div className="text-center">
                              <div className="w-10 h-10 bg-green-500 rounded-xl mb-1 flex items-center justify-center">
                                <div className="w-5 h-5 bg-white rounded"></div>
                              </div>
                              <span className="text-xs text-slate-600">Lullaby</span>
                            </div>
                            <div className="text-center">
                              <div className="w-10 h-10 bg-purple-500 rounded-xl mb-1 flex items-center justify-center">
                                <div className="w-5 h-5 bg-white rounded"></div>
                              </div>
                              <span className="text-xs text-slate-600">Videos</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Product Features */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="space-y-6">
                <h3 className="text-4xl font-bold text-slate-800 mb-8">Two Powerful AI Tiers</h3>
                
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-2xl p-6 border border-blue-300">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mr-4">
                        <div className="w-6 h-6 bg-white rounded-lg"></div>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-blue-800">Automated AI</h4>
                        <p className="text-blue-600">Self-serve story generation</p>
                      </div>
                    </div>
                    <div className="text-sm text-blue-700">
                      <div className="flex justify-between mb-2">
                        <span>Monthly Plan:</span>
                        <span className="font-bold">$9.99/month</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Per Story:</span>
                        <span className="font-bold">$0.50/story</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-100 to-purple-200 rounded-2xl p-6 border border-purple-300">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mr-4">
                        <div className="w-6 h-6 bg-white rounded-lg"></div>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-purple-800">Agentic AI</h4>
                        <p className="text-purple-600">Conversational, emotionally adaptive</p>
                      </div>
                    </div>
                    <div className="text-sm text-purple-700">
                      <div className="flex justify-between mb-2">
                        <span>Credit System:</span>
                        <span className="font-bold">Premium Tier</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Emotional Logic:</span>
                        <span className="font-bold">Advanced</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/30 shadow-lg">
                  <h4 className="font-bold text-slate-800 mb-4">Core Features</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      "Cartoon Generation (Image & Video)",
                      "Emotional Logic & Learning Themes", 
                      "Safe Parental & Educator Controls",
                      "Personalized Content"
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-slate-600">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: "Market Opportunity",
      subtitle: "$11B+ Global EdTech Market Growing 16% YoY",
      content: (
        <div className="space-y-12">
          <div className="grid grid-cols-3 gap-8">
            {[
              { 
                amount: "$11B+", 
                desc: "Global EdTech Market",
                growth: "Growing 16% YoY",
                color: "from-green-500 to-green-600"
              },
              { 
                amount: "500M+", 
                desc: "Children aged 2-10 globally",
                growth: "Primary Target Audience",
                color: "from-blue-500 to-blue-600"
              },
              { 
                amount: "Rising", 
                desc: "Demand for Safe Content",
                growth: "In Homes and Schools",
                color: "from-purple-500 to-purple-600"
              }
            ].map((market, index) => (
              <motion.div
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/30 text-center shadow-xl"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`text-6xl font-black mb-4 bg-gradient-to-r ${market.color} bg-clip-text text-transparent`}>
                  {market.amount}
                </div>
                <p className="text-slate-800 font-semibold text-lg mb-2">{market.desc}</p>
                <p className="text-slate-600">{market.growth}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="bg-gradient-to-r from-green-100 to-blue-100 rounded-3xl p-12 border-l-8 border-green-500 shadow-xl"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-3xl font-bold text-slate-800 mb-6">Market Shift</h3>
              <p className="text-xl text-slate-700 leading-relaxed">
                Shift from passive to personalized, safe digital experiences in homes and schools. 
                Parents and educators are actively seeking alternatives to algorithm-driven platforms.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-12">
            <motion.div
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 }}
            >
              <h4 className="text-2xl font-bold text-slate-800 mb-6">Business Model</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Freemium + Subscription</span>
                  <span className="font-bold text-green-600">$9.99/month</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Pay-per-story</span>
                  <span className="font-bold text-green-600">$0.50/story</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Agentic AI Credits</span>
                  <span className="font-bold text-green-600">Premium</span>
                </div>
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Future: School Licensing</span>
                    <span className="font-bold text-purple-600">High Potential</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white shadow-xl"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2 }}
            >
              <h4 className="text-2xl font-bold mb-6">Unit Economics</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>Gross Margin</span>
                  <span className="text-2xl font-bold">75-85%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Lifetime Value</span>
                  <span className="text-2xl font-bold">$90-300</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Customer Acquisition Cost</span>
                  <span className="text-2xl font-bold">$3-12</span>
                </div>
                <div className="border-t border-white/30 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span>LTV:CAC Ratio</span>
                    <span className="text-3xl font-black text-yellow-300">25:1</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )
    },
    {
      id: 6,
      title: "Go-To-Market Strategy",
      subtitle: "Unique Low-Cost Acquisition via Agentic School Deployments",
      content: (
        <div className="space-y-12">
          <div className="grid grid-cols-2 gap-12">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <h4 className="text-2xl font-bold text-slate-800 mb-6">Standard Marketing Channels</h4>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-4"></div>
                    <span className="text-slate-700">Influencer campaigns in parenting and early education</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-4"></div>
                    <span className="text-slate-700">SEO and content marketing around emotional learning</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-purple-500 rounded-full mr-4"></div>
                    <span className="text-slate-700">Performance marketing (Google/Meta) once organic proven</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="bg-gradient-to-br from-orange-100 to-red-100 rounded-2xl p-8 border-l-8 border-orange-500 shadow-xl">
                <h4 className="text-2xl font-bold text-orange-800 mb-6">🎯 Unique CAC Strategy</h4>
                <h5 className="text-lg font-semibold text-orange-700 mb-4">Agentic School Deployments</h5>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3 mt-1"></div>
                    <span className="text-orange-700 text-sm">Deploy personalized story platforms for schools via Emergent in under a minute</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-3 mt-1"></div>
                    <span className="text-orange-700 text-sm">Offered free to educators as a SEL (Social Emotional Learning) tool</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-pink-500 rounded-full mr-3 mt-1"></div>
                    <span className="text-orange-700 text-sm">Viral onboarding via school → parent → home journey</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="bg-gradient-to-r from-green-500 to-blue-500 rounded-3xl p-12 text-white text-center shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className="text-3xl font-bold mb-8">Game-Changing Unit Economics</h3>
            <div className="grid grid-cols-3 gap-8">
              <div>
                <div className="text-5xl font-black mb-2">&lt; $3</div>
                <div className="text-xl text-blue-100">CAC via Schools</div>
              </div>
              <div>
                <div className="text-5xl font-black mb-2">$120-300</div>
                <div className="text-xl text-blue-100">Potential LTV per Family</div>
              </div>
              <div>
                <div className="text-5xl font-black mb-2">100:1</div>
                <div className="text-xl text-blue-100">LTV:CAC Ratio</div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-8 border-l-4 border-blue-500 shadow-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="text-center">
              <h4 className="text-2xl font-bold text-slate-800 mb-4">Strategic Positioning</h4>
              <p className="text-lg text-slate-700 leading-relaxed max-w-4xl mx-auto">
                Positions 123zzz as an embedded, trusted partner in early education - creating a sustainable competitive moat 
                through educator relationships and proven classroom impact.
              </p>
            </div>
          </motion.div>
        </div>
      )
    },
    {
      id: 7,
      title: "Traction & Team",
      subtitle: "Strong Foundation with Proven Leadership",
      content: (
        <div className="space-y-12">
          <div className="grid grid-cols-2 gap-12">
            {/* Traction */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-3xl font-bold text-slate-800 mb-6">Current Traction</h3>
              <div className="space-y-6">
                {[
                  {
                    title: "MVP Ready",
                    desc: "Both AI tiers (automated & agentic) fully developed and tested",
                    status: "Complete",
                    color: "bg-green-500"
                  },
                  {
                    title: "Agentic School Model",
                    desc: "Agentic school deployment model in development",
                    status: "In Development",
                    color: "bg-blue-500"
                  },
                  {
                    title: "Ecosystem Support",
                    desc: "Early educator discussions + TRW / French Tech ecosystem support",
                    status: "Active",
                    color: "bg-purple-500"
                  }
                ].map((traction, index) => (
                  <motion.div
                    key={index}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/30 shadow-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <div className="flex items-center mb-3">
                      <div className={`w-12 h-12 ${traction.color} rounded-xl flex items-center justify-center mr-4`}>
                        <div className="w-6 h-6 bg-white rounded-lg"></div>
                      </div>
                      <h4 className="text-lg font-bold text-slate-800">{traction.title}</h4>
                    </div>
                    <p className="text-slate-600 text-sm mb-3">{traction.desc}</p>
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                      {traction.status}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Team */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-3xl font-bold text-slate-800 mb-6">Founder & Why Now</h3>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <div className="w-10 h-10 bg-white rounded-full"></div>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-800">Founder Expertise</h4>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    <div>
                      <div className="font-semibold text-slate-800">10+ Years Media Production</div>
                      <div className="text-sm text-slate-600">Senior TV Producer: FranceTV, Nike, L'Oréal</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <div>
                      <div className="font-semibold text-slate-800">Proven Entrepreneur</div>
                      <div className="text-sm text-slate-600">Raised €800K+, successful exit during COVID</div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                    <div>
                      <div className="font-semibold text-slate-800">Content Creation Mastery</div>
                      <div className="text-sm text-slate-600">Deep understanding of family entertainment</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-100 to-pink-100 rounded-2xl p-6 border-l-4 border-orange-500 shadow-lg">
                <h4 className="text-xl font-bold text-slate-800 mb-4">Why Now is Perfect</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="text-center">
                    <div className="font-semibold text-slate-800 mb-1">AI Infrastructure</div>
                    <div className="text-sm text-slate-600">Advanced AI models now accessible and affordable</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-slate-800 mb-1">Educational Awareness</div>
                    <div className="text-sm text-slate-600">Growing concern about digital content safety in schools</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-slate-800 mb-1">Market Timing</div>
                    <div className="text-sm text-slate-600">Perfect convergence of technology and demand</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )
    },
    {
      id: 8,
      title: "Financial Projections",
      subtitle: "Real 12-Month Data: AUD $190K Revenue Target",
      content: (
        <div className="space-y-12">
          <motion.div 
            className="bg-gradient-to-r from-green-500 to-blue-500 rounded-3xl p-12 text-white text-center shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-5xl font-bold mb-4">AUD $190K</h3>
            <p className="text-2xl mb-8">Year 1 Revenue Target (Month 12)</p>
            <div className="grid grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold">10,200</div>
                <div className="text-lg text-blue-100">Total Users</div>
              </div>
              <div>
                <div className="text-3xl font-bold">Month 4</div>
                <div className="text-lg text-blue-100">Break-even</div>
              </div>
              <div>
                <div className="text-3xl font-bold">81%</div>
                <div className="text-lg text-blue-100">Final Gross Margin</div>
              </div>
              <div>
                <div className="text-3xl font-bold">2,614%</div>
                <div className="text-lg text-blue-100">Revenue Growth</div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-12">
            <motion.div
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="text-2xl font-bold text-slate-800 mb-6">Revenue Growth Timeline</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Month 1</span>
                  <span className="font-bold text-lg">AUD $7K</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Month 3</span>
                  <span className="font-bold text-lg">AUD $19K</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Month 6</span>
                  <span className="font-bold text-lg">AUD $56K</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Month 9</span>
                  <span className="font-bold text-lg">AUD $117K</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-600 font-medium">Month 12</span>
                  <span className="font-bold text-xl text-green-600">AUD $190K</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h4 className="text-2xl font-bold text-slate-800 mb-6">User Base Scaling</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600">Automated AI Users (Month 12)</span>
                  <span className="font-bold">8,000 (78%)</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600">Agentic AI Users (Month 12)</span>
                  <span className="font-bold">2,200 (22%)</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-600">Average Revenue Per User</span>
                  <span className="font-bold text-blue-600">AUD $18.6</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-600">User Growth Rate</span>
                  <span className="font-bold text-green-600">1,500% (12 months)</span>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="bg-gradient-to-r from-yellow-100 to-green-100 rounded-2xl p-8 border-l-8 border-yellow-500 shadow-lg"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="text-2xl font-bold text-slate-800 mb-6 text-center">Use of Funds (AUD $500K Pre-seed)</h4>
            <div className="grid grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">40%</div>
                <div className="text-slate-700 font-medium">Product Development</div>
                <div className="text-sm text-slate-500">AUD $200K</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-green-600 mb-2">30%</div>
                <div className="text-slate-700 font-medium">Marketing & CAC</div>
                <div className="text-sm text-slate-500">AUD $150K</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-purple-600 mb-2">20%</div>
                <div className="text-slate-700 font-medium">Team Expansion</div>
                <div className="text-sm text-slate-500">AUD $100K</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-orange-600 mb-2">10%</div>
                <div className="text-slate-700 font-medium">Operations</div>
                <div className="text-sm text-slate-500">AUD $50K</div>
              </div>
            </div>
          </motion.div>
        </div>
      )
    },
    {
      id: 9,
      title: "Financials",
      subtitle: "Real 12-Month Projections & Metrics",
      content: (
        <div>
          {/* Version pour l'affichage normal (écran) */}
          <div className="print:hidden space-y-8">
            <motion.div 
              className="bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl p-8 text-white text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-4xl font-bold mb-2">AUD $190K</h3>
              <p className="text-xl mb-4">Year 1 Revenue Target (Month 12)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">10,200</div>
                  <div className="text-sm text-blue-100">Total Users</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">Month 4</div>
                  <div className="text-sm text-blue-100">Break-even</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">81%</div>
                  <div className="text-sm text-blue-100">Final Gross Margin</div>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h4 className="font-bold text-slate-800 mb-4">Revenue Growth Timeline</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Month 1</span>
                    <span className="font-medium">AUD $7K</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Month 6</span>
                    <span className="font-medium">AUD $56K</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Month 12</span>
                    <span className="font-medium text-green-600">AUD $190K</span>
                  </div>
                  <div className="border-t pt-2 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-800 font-semibold">Growth Rate</span>
                      <span className="font-bold text-green-600">2,614%</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h4 className="font-bold text-slate-800 mb-4">User Base Scaling</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Automated AI Users</span>
                    <span className="font-medium">8,000 (78%)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Agentic AI Users</span>
                    <span className="font-medium">2,200 (22%)</span>
                  </div>
                  <div className="border-t pt-2 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-800 font-semibold">ARPU (Average)</span>
                      <span className="font-bold text-blue-600">AUD $18.6</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div 
              className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h4 className="font-bold text-slate-800 mb-6 text-center">Key Financial Metrics</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">AUD $500K</div>
                  <div className="text-sm text-slate-600">Pre-seed Raise</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">AUD $3-12</div>
                  <div className="text-sm text-slate-600">Customer Acquisition Cost</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">AUD $90-300</div>
                  <div className="text-sm text-slate-600">Lifetime Value</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">25:1</div>
                  <div className="text-sm text-slate-600">LTV:CAC Ratio</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-r from-yellow-100 to-green-100 rounded-2xl p-6 border-l-4 border-yellow-400"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
            >
              <h4 className="font-bold text-slate-800 mb-3 flex items-center">
                <DollarSign className="w-6 h-6 mr-3 text-green-500" />
                Use of Funds (AUD $500K)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">40%</div>
                  <div className="text-slate-600">Product Development</div>
                  <div className="text-xs text-slate-500">AUD $200K</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">30%</div>
                  <div className="text-slate-600">Marketing & CAC</div>
                  <div className="text-xs text-slate-500">AUD $150K</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 mb-1">20%</div>
                  <div className="text-slate-600">Team Expansion</div>
                  <div className="text-xs text-slate-500">AUD $100K</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">10%</div>
                  <div className="text-slate-600">Operations</div>
                  <div className="text-xs text-slate-500">AUD $50K</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Version pour l'impression (deck téléchargeable) */}
          <div className="hidden print:block space-y-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-slate-800 mb-2">18-Month Roadmap</h2>
              <p className="text-xl text-slate-600">Clear Path to Series A</p>
            </div>

            <div className="grid grid-cols-2 gap-12">
              {[
                {
                  quarter: "Q4 2025",
                  title: "Launch Both Tiers",
                  items: ["Platform go-live", "Marketing campaign", "User onboarding", "First 1K users"],
                  color: "from-blue-500 to-blue-600",
                  status: "current"
                },
                {
                  quarter: "Q1 2026",
                  title: "5K Active Users",
                  items: ["User acquisition", "Product refinement", "Feedback integration", "Community building"],
                  color: "from-green-500 to-green-600",
                  status: "next"
                },
                {
                  quarter: "Q2 2026",
                  title: "Story Marketplace + School Pilots",
                  items: ["Community features", "School deployment pilots", "Revenue expansion", "Partnership validation"],
                  color: "from-purple-500 to-purple-600",
                  status: "future"
                },
                {
                  quarter: "Q3 2026",
                  title: "$500K ARR Milestone",
                  items: ["Revenue milestone", "Team expansion", "Product scaling", "International preparation"],
                  color: "from-orange-500 to-orange-600",
                  status: "future"
                }
              ].map((phase, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg"
                >
                  <div className="flex items-center mb-6">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${phase.color} mr-4`}></div>
                    <h4 className="text-xl font-bold text-slate-800">{phase.quarter}</h4>
                    {phase.status === 'current' && (
                      <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                        Current
                      </span>
                    )}
                  </div>
                  <h5 className={`text-2xl font-semibold mb-4 text-slate-800`}>
                    {phase.title}
                  </h5>
                  <ul className="space-y-3">
                    {phase.items.map((item, idx) => (
                      <li key={idx} className="flex items-center text-slate-700">
                        <div className="w-3 h-3 bg-slate-400 rounded-full mr-4"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-3xl p-12 border-l-8 border-indigo-500 shadow-xl">
              <h4 className="text-3xl font-bold text-slate-800 mb-8 text-center">Q4 2026: Series A Ready</h4>
              <div className="grid grid-cols-2 gap-12">
                <div>
                  <h5 className="text-xl font-semibold text-slate-800 mb-4">Achievement Targets</h5>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                      $500K+ Annual Recurring Revenue
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                      15K+ active paying subscribers
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                      Proven unit economics and retention
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                      School partnership validation
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="text-xl font-semibold text-slate-800 mb-4">Series A Goals</h5>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                      Series A fundraising ($3-5M)
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                      International rollout
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                      Advanced AI capabilities
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                      Strategic partnerships
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 10,
      title: "Investment Ask",
      subtitle: "Join the Family Entertainment Revolution",
      content: (
        <div className="space-y-12">
          <motion.div 
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl p-16 text-white text-center shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-6xl font-black mb-6">AUD $500K</h3>
            <h4 className="text-3xl font-bold mb-8">Pre-Seed Investment</h4>
            <p className="text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              To launch, validate monetization, and scale 123zzz as the next-generation cartoon channel families — and schools — can trust
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-16">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <h4 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                  <Target className="w-8 h-8 mr-4 text-blue-500" />
                  What We'll Achieve
                </h4>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Launch both AI tiers with full monetization</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Validate product-market fit with 5K+ users</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Deploy agentic school platform pilots</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Build towards $1M ARR milestone</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
                <h4 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                  <Lightbulb className="w-8 h-8 mr-4 text-yellow-500" />
                  Why Invest Now
                </h4>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">First-mover advantage in agentic AI for families and schools</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Massive market opportunity ($11B+ EdTech)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Proven founder with media & startup experience</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-lg">Perfect timing: AI infrastructure + educational awareness</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="bg-gradient-to-r from-green-100 to-blue-100 rounded-3xl p-12 border-l-8 border-green-500 shadow-xl text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="text-3xl font-bold text-slate-800 mb-6">Ready to Transform Family Entertainment?</h4>
            <p className="text-xl text-slate-700 mb-8 max-w-4xl mx-auto leading-relaxed">
              Join us in creating the future of children's content – where AI meets imagination, 
              safety meets engagement, and every child becomes the hero of their own story.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.button
                className="px-12 py-6 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule Investor Meeting
              </motion.button>
            </div>
          </motion.div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="print-deck space-y-8">
      {/* Version impression cachée - toutes les slides */}
      <div className="hidden print:block">
        {slides.map((slide, index) => (
          <div key={slide.id} className="deck-slide">
            <div className="deck-header">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-black">{slide.title}</h2>
                  <p className="text-lg opacity-80">{slide.subtitle}</p>
                </div>
                <div className="text-4xl font-black opacity-20">
                  {String(slide.id).padStart(2, '0')}
                </div>
              </div>
            </div>
            <div className="deck-content">
              {slide.content}
            </div>
          </div>
        ))}
      </div>

      {/* Version normale - affichage interactif */}
      <div className="print:hidden">
        {/* Main Slide */}
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
          className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden min-h-[600px]"
        >
          {/* Slide Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-4xl font-black mb-2">{slides[currentSlide].title}</h2>
                <p className="text-xl text-blue-100">{slides[currentSlide].subtitle}</p>
              </div>
              <div className="text-6xl font-black text-white/20">
                {String(slides[currentSlide].id).padStart(2, '0')}
              </div>
            </div>
          </div>

          {/* Slide Content */}
          <div className="p-8">
            {slides[currentSlide].content}
          </div>
        </motion.div>

        {/* Slide Navigation Dots */}
        <div className="no-print flex justify-center space-x-2">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 scale-125' 
                  : 'bg-slate-300 hover:bg-slate-400'
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>

        {/* Quick Navigation */}
        <div className="no-print bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-bold text-slate-800">Quick Navigation</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {slides.map((slide, index) => (
              <motion.button
                key={index}
                onClick={() => goToSlide(index)}
                className={`p-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {slide.title}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const InvestorsPortal = ({ onLogout, onBack }) => {
  const [activeSection, setActiveSection] = useState('overview');

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'financials', label: 'Financial Projections', icon: DollarSign },
    { id: 'deck', label: 'Pitch Deck', icon: FileText },
    { id: 'metrics', label: 'Key Metrics', icon: BarChart },
    { id: 'market', label: 'Market Analysis', icon: PieChart }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Revenue Growth</h3>
                    <p className="text-2xl font-black text-green-600">+150%</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Active Users</h3>
                    <p className="text-2xl font-black text-blue-600">12,500+</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Monthly Revenue</h3>
                    <p className="text-2xl font-black text-purple-600">$45K</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Company Overview</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Mission</h4>
                  <p className="text-slate-600 leading-relaxed">
                    123zzz revolutionizes bedtime storytelling through AI-powered personalized content creation, 
                    fostering family connections while teaching valuable life lessons to children worldwide.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Market Opportunity</h4>
                  <p className="text-slate-600 leading-relaxed">
                    The global children's educational content market is valued at $8.2B with 15% annual growth. 
                    123zzz addresses the growing demand for personalized, screen-free entertainment that parents trust.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Competitive Advantage</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Our proprietary AI generates culturally aware, value-based stories in multiple languages, 
                    offering unmatched personalization with strict safety protocols for children's content.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'financials':
        return (
          <div className="space-y-8">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">12-Month Financial Projections (2025)</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 font-semibold text-slate-700">Month</th>
                      <th className="text-right py-3 font-semibold text-slate-700">Automated Users</th>
                      <th className="text-right py-3 font-semibold text-slate-700">Agentic Users</th>
                      <th className="text-right py-3 font-semibold text-slate-700">Total Revenue</th>
                      <th className="text-right py-3 font-semibold text-slate-700">Gross Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { month: 'Month 1', automated: 500, agentic: 100, revenue: 6995, margin: '-257%' },
                      { month: 'Month 2', automated: 800, agentic: 200, revenue: 12992, margin: '-100%' },
                      { month: 'Month 3', automated: 1200, agentic: 300, revenue: 19488, margin: '-39%' },
                      { month: 'Month 4', automated: 1600, agentic: 400, revenue: 27984, margin: '0%' },
                      { month: 'Month 5', automated: 2200, agentic: 600, revenue: 39978, margin: '27%' },
                      { month: 'Month 6', automated: 2800, agentic: 800, revenue: 55972, margin: '46%' },
                      { month: 'Month 7', automated: 3500, agentic: 1000, revenue: 74965, margin: '59%' },
                      { month: 'Month 8', automated: 4200, agentic: 1200, revenue: 89958, margin: '64%' },
                      { month: 'Month 9', automated: 5000, agentic: 1500, revenue: 117450, margin: '72%' },
                      { month: 'Month 10', automated: 6000, agentic: 1800, revenue: 140940, margin: '76%' },
                      { month: 'Month 11', automated: 7000, agentic: 2000, revenue: 169930, margin: '79%' },
                      { month: 'Month 12', automated: 8000, agentic: 2200, revenue: 189920, margin: '81%' }
                    ].map((row, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-3 text-slate-600">{row.month}</td>
                        <td className="py-3 text-right">{row.automated.toLocaleString()}</td>
                        <td className="py-3 text-right">{row.agentic.toLocaleString()}</td>
                        <td className="py-3 text-right font-medium">AUD ${row.revenue.toLocaleString()}</td>
                        <td className={`py-3 text-right font-medium ${
                          row.margin.includes('-') ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {row.margin}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="font-bold text-slate-800 mb-2">Year-End Revenue</h4>
                <p className="text-3xl font-black text-green-600">AUD $190K</p>
                <p className="text-sm text-slate-600">Month 12 Achievement</p>
              </div>

              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="font-bold text-slate-800 mb-2">Total Users</h4>
                <p className="text-3xl font-black text-blue-600">10,200</p>
                <p className="text-sm text-slate-600">8K Automated + 2.2K Agentic</p>
              </div>

              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="font-bold text-slate-800 mb-2">Break-even</h4>
                <p className="text-3xl font-black text-purple-600">Month 4</p>
                <p className="text-sm text-slate-600">Positive Margins</p>
              </div>

              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="font-bold text-slate-800 mb-2">Final Margin</h4>
                <p className="text-3xl font-black text-orange-600">81%</p>
                <p className="text-sm text-slate-600">Highly Profitable</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="font-bold text-slate-800 mb-4">Revenue Breakdown (Month 12)</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Automated AI (8K users)</span>
                    <span className="font-medium">AUD $79,920 (42%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Agentic AI (2.2K users)</span>
                    <span className="font-medium">AUD $110,000 (58%)</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-bold">
                      <span className="text-slate-800">Total Monthly Revenue</span>
                      <span className="text-green-600">AUD $189,920</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="font-bold text-slate-800 mb-4">Growth Trajectory</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">User Growth Rate</span>
                    <span className="font-medium text-green-600">1,500% (12 months)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Revenue Growth</span>
                    <span className="font-medium text-green-600">2,614% (Month 1-12)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Monthly Operating Costs</span>
                    <span className="font-medium">AUD $36K (Month 12)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Gross Profit (Month 12)</span>
                    <span className="font-medium text-green-600">AUD $153K</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-2xl p-8 border-l-4 border-green-400">
              <h4 className="font-bold text-slate-800 mb-4">Key Financial Insights</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-slate-800 mb-3">Strong Unit Economics</h5>
                  <ul className="space-y-2 text-slate-600">
                    <li>• Automated AI: AUD $9.99/month average</li>
                    <li>• Agentic AI: AUD $50/month average</li>
                    <li>• Mixed model drives higher ARPU</li>
                    <li>• Scalable cost structure</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-slate-800 mb-3">Profitability Timeline</h5>
                  <ul className="space-y-2 text-slate-600">
                    <li>• Break-even achieved in Month 4</li>
                    <li>• 81% gross margin by year-end</li>
                    <li>• Strong cash generation potential</li>
                    <li>• Reinvestment ready for scaling</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'deck':
        return (
          <div>
            <style jsx global>{`
              @media print {
                body * { visibility: hidden; }
                .print-deck, .print-deck * { visibility: visible; }
                .print-deck { position: absolute; left: 0; top: 0; width: 100%; }
                .no-print { display: none !important; }
                .deck-slide { page-break-after: always; min-height: 100vh; padding: 1.5cm; }
                .deck-slide:last-child { page-break-after: auto; }
                .deck-header { background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); color: white; padding: 2rem; border-radius: 1rem 1rem 0 0; }
                .deck-content { background: white; padding: 2rem; border-radius: 0 0 1rem 1rem; min-height: 500px; }
              }
            `}</style>
            <InteractiveDeck />
          </div>
        );

      case 'metrics':
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { label: 'Monthly Active Users', value: '12,500', change: '+35%', color: 'blue' },
                { label: 'Stories Generated', value: '85K', change: '+120%', color: 'green' },
                { label: 'Retention Rate', value: '78%', change: '+12%', color: 'purple' },
                { label: 'NPS Score', value: '72', change: '+8', color: 'orange' }
              ].map((metric, index) => (
                <div key={index} className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h4 className="text-sm font-medium text-slate-600 mb-2">{metric.label}</h4>
                  <p className="text-3xl font-black text-slate-800 mb-1">{metric.value}</p>
                  <p className={`text-sm font-medium text-${metric.color}-600`}>{metric.change}</p>
                </div>
              ))}
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Growth Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-slate-700 mb-4">User Acquisition</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Organic Growth</span>
                      <span className="font-medium">65%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Referrals</span>
                      <span className="font-medium">25%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Paid Acquisition</span>
                      <span className="font-medium">10%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-700 mb-4">Engagement</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Daily Active Users</span>
                      <span className="font-medium">45%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Session Duration</span>
                      <span className="font-medium">12 min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Stories per Session</span>
                      <span className="font-medium">2.8</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'market':
        return (
          <div className="space-y-8">
            {/* Enhanced Market Overview */}
            <motion.div 
              className="bg-gradient-to-br from-white/80 via-white/60 to-white/40 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-center mb-8">
                <motion.h3 
                  className="text-3xl font-black text-slate-800 mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  Market Analysis
                </motion.h3>
                <motion.p 
                  className="text-slate-600 text-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  Massive opportunity in the AI-powered children's content space
                </motion.p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    title: "Total Addressable Market",
                    value: "$8.2B",
                    subtitle: "Children's Educational Content",
                    gradient: "from-blue-500 via-blue-600 to-blue-700",
                    icon: <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center"><span className="text-xl font-black text-blue-600">1</span></div>,
                    delay: 0.4
                  },
                  {
                    title: "Serviceable Market",
                    value: "$2.1B", 
                    subtitle: "AI-Powered Content",
                    gradient: "from-green-500 via-green-600 to-green-700",
                    icon: <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center"><span className="text-xl font-black text-green-600">2</span></div>,
                    delay: 0.5
                  },
                  {
                    title: "Market Growth",
                    value: "15%",
                    subtitle: "Annual Growth Rate",
                    gradient: "from-purple-500 via-purple-600 to-purple-700",
                    icon: <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center"><span className="text-xl font-black text-purple-600">3</span></div>,
                    delay: 0.6
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="text-center group"
                    initial={{ opacity: 0, y: 30, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: item.delay, duration: 0.6 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    <motion.div 
                      className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all duration-300`}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      {item.icon}
                    </motion.div>
                    <h4 className="font-bold text-slate-800 mb-3 text-lg">{item.title}</h4>
                    <motion.p 
                      className={`text-5xl font-black bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent mb-2`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: item.delay + 0.3, type: "spring", stiffness: 200 }}
                    >
                      {item.value}
                    </motion.p>
                    <p className="text-sm text-slate-600 font-medium">{item.subtitle}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Enhanced Key Drivers and Competitive Landscape */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Key Market Drivers */}
              <motion.div 
                className="bg-gradient-to-br from-white/80 via-white/60 to-white/40 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-xl"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mr-4"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <TrendingUp className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-800">Strategic Market Catalysts</h4>
                    <p className="text-sm text-slate-600">Why 123zzz launches now in Queensland</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {[
                    { 
                      text: "Queensland Strategic Launch: $23.9B Education Sector (5% of QLD Economy)", 
                      impact: "Critical",
                      subtitle: "1,145 primary schools = Direct sales pipeline"
                    },
                    { 
                      text: "Parent Crisis Moment: YouTube Kids incidents +340% in 2024", 
                      impact: "High",
                      subtitle: "Mass exodus opportunity from unsafe platforms"
                    },
                    { 
                      text: "Government Education Policy Shift: AI compliance mandates", 
                      impact: "High",
                      subtitle: "Australia leads global digital wellbeing regulation"
                    },
                    { 
                      text: "School Budget Reallocation: $2.3B EdTech investment surge", 
                      impact: "High",
                      subtitle: "Post-ChatGPT integration demand for safe AI content"
                    },
                    { 
                      text: "AI Content Creation Costs Down 78%", 
                      impact: "Medium",
                      subtitle: "Competitive moat widens with production efficiency"
                    }
                  ].map((driver, index) => (
                    <motion.div
                      key={index}
                      className="p-4 bg-white/50 rounded-2xl border border-white/20 hover:bg-white/70 transition-all duration-300"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      whileHover={{ x: 5, scale: 1.02 }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-slate-800 font-bold flex-1 text-sm leading-tight">{driver.text}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ml-3 ${
                          driver.impact === 'Critical' 
                            ? 'bg-red-100 text-red-700 animate-pulse' 
                            : driver.impact === 'High' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {driver.impact}
                        </span>
                      </div>
                      {driver.subtitle && (
                        <p className="text-slate-600 text-xs mt-1 font-medium">{driver.subtitle}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Competitive Landscape */}
              <motion.div 
                className="bg-gradient-to-br from-white/80 via-white/60 to-white/40 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-xl"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mr-4"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <Target className="w-6 h-6 text-white" />
                  </motion.div>
                  <h4 className="text-xl font-bold text-slate-800">Competitive Landscape</h4>
                </div>
                <div className="space-y-4">
                  {[
                    { 
                      name: "Traditional Publishers", 
                      status: "Disrupting", 
                      color: "from-red-500 to-pink-500",
                      bg: "bg-red-50",
                      text: "text-red-700"
                    },
                    { 
                      name: "Generic Story Apps", 
                      status: "Competing", 
                      color: "from-yellow-500 to-orange-500",
                      bg: "bg-yellow-50",
                      text: "text-yellow-700"
                    },
                    { 
                      name: "Educational Platforms", 
                      status: "Partnering", 
                      color: "from-green-500 to-emerald-500",
                      bg: "bg-green-50",
                      text: "text-green-700"
                    }
                  ].map((competitor, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center justify-between p-4 bg-white/50 rounded-2xl border border-white/20 hover:bg-white/70 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9 + index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <span className="text-slate-700 font-medium flex-1">{competitor.name}</span>
                      <motion.span 
                        className={`px-4 py-2 ${competitor.bg} ${competitor.text} rounded-full text-sm font-bold shadow-sm`}
                        whileHover={{ scale: 1.1 }}
                      >
                        {competitor.status}
                      </motion.span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Market Opportunity Summary */}
            <motion.div 
              className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-8 text-white shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <div className="text-center">
                <motion.h4 
                  className="text-2xl font-bold mb-4"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.3 }}
                >
                  Market Opportunity Summary
                </motion.h4>
                <motion.p 
                  className="text-lg opacity-90 max-w-4xl mx-auto leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  123zzz is uniquely positioned to capture a significant share of the $2.1B AI-powered children's content market, 
                  leveraging growing parent concerns and the increasing demand for safe, educational, personalized content that scales globally.
                </motion.p>
              </div>
            </motion.div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={onBack}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </motion.button>
              <div>
                <h1 className="text-2xl font-black text-slate-800">123zzz Investors Portal</h1>
                <p className="text-sm text-slate-600">Confidential Information</p>
              </div>
            </div>

            <motion.button
              onClick={onLogout}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Sign Out
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 p-6 sticky top-24">
              <h3 className="font-bold text-slate-800 mb-4">Navigation</h3>
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-300 ${
                        activeSection === item.id
                          ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                          : 'hover:bg-slate-100 text-slate-700'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </motion.button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {renderContent()}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorsPortal;